import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faChartLine,
  faExclamationTriangle,
  faLayerGroup,
} from "@fortawesome/free-solid-svg-icons";
import { detectCsvDataType } from "../../../utils/csvDataType";
import RawFallback from "../../Shared/RawFallback/RawFallback";
import styles from "./CSVPatternDetection.module.css";

const MAX_BULLET_LEN = 140;
const truncate = (s) => (s.length > MAX_BULLET_LEN ? s.slice(0, MAX_BULLET_LEN).trim() + "…" : s);

const dedupe = (items) => {
  const seen = new Set();
  return items.filter((item) => {
    const norm = item.toLowerCase().slice(0, 60).replace(/\s+/g, " ");
    if (seen.has(norm)) return false;
    seen.add(norm);
    return true;
  });
};

const getBullets = (content) => {
  if (!content || !Array.isArray(content)) return [];
  return content
    .map((c) => (c.type === "bullet" ? c.text : c.type === "keyValue" ? `${c.key}: ${c.value}` : c.text))
    .filter(Boolean)
    .map((t) => String(t).trim())
    .filter((t) => t.length > 10);
};

const CSVPatternDetection = ({ data, rawText, analysisData }) => {
  if (!data) {
    return (
      <div className={styles.container}>
        <div className={styles.emptyState}>
          <p>No pattern detection data available</p>
          {rawText && <RawFallback rawText={rawText} accentColor="#22c55e" />}
        </div>
      </div>
    );
  }

  const dataType = detectCsvDataType(analysisData?.columns);
  const isML = dataType === "ml_scientific";

  const sections = [];
  const titleMap = {
    "Training Trends": { icon: faChartLine, color: "#10b981" },
    "Key Observations": { icon: faLayerGroup, color: "#06b6d4" },
    "Anomalies": { icon: faExclamationTriangle, color: "#ef4444" },
    "Pattern Identification": { icon: faLayerGroup, color: "#10b981" },
    "Value Patterns": { icon: faChartLine, color: "#06b6d4" },
    "Relationship Patterns": { icon: faChartLine, color: "#f59e0b" },
    "Business Patterns": { icon: faChartLine, color: "#8b5cf6" },
    "Anomaly Patterns": { icon: faExclamationTriangle, color: "#ef4444" },
    "Timeline & Sequence Patterns": { icon: faChartLine, color: "#06b6d4" },
    "Clustering Observations": { icon: faLayerGroup, color: "#8b5cf6" },
  };

  if (data.sections) {
    const order = isML
      ? ["Training Trends", "Key Observations", "Anomalies"]
      : ["Pattern Identification", "Value Patterns", "Relationship Patterns", "Anomaly Patterns", "Business Patterns", "Timeline & Sequence Patterns", "Clustering Observations"];
    const seen = new Set();
    for (const name of order) {
      const sec = data.sections.find((s) => {
        const n = (s.name || "").toLowerCase();
        const target = name.toLowerCase();
        return n.includes(target) || target.includes(n);
      });
      if (sec && sec.content?.length && !seen.has(sec.name)) {
        seen.add(sec.name);
        const bullets = dedupe(getBullets(sec.content));
        if (bullets.length > 0) {
          const info = titleMap[name] || titleMap[sec.name] || { icon: faSearch, color: "#10b981" };
          sections.push({ name: sec.name, bullets, ...info });
        }
      }
    }
    data.sections.forEach((sec) => {
      if (seen.has(sec.name) || !sec.content?.length) return;
      const bullets = dedupe(getBullets(sec.content));
      if (bullets.length > 0) {
        const info = titleMap[sec.name] || { icon: faSearch, color: "#10b981" };
        sections.push({ name: sec.name, bullets, ...info });
      }
    });
  }

  if (sections.length === 0 && rawText) {
    const parts = rawText.split(/SECTION:\s*/i).filter(Boolean);
    const allBullets = [];
    parts.forEach((p) => {
      const bullets = (p.match(/- ([^\n]+)/g) || []).map((b) => b.replace(/^- /, "").trim()).filter((t) => t.length > 15);
      allBullets.push(...bullets);
    });
    if (allBullets.length > 0) {
      sections.push({
        name: "Key Patterns",
        bullets: dedupe(allBullets).slice(0, 8),
        icon: faSearch,
        color: "#10b981",
      });
    }
  }

  const hasAnyData = sections.length > 0;

  if (!hasAnyData) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.headerIcon}>
            <FontAwesomeIcon icon={faSearch} />
          </div>
          <div>
            <h2 className={styles.title}>Pattern Detection</h2>
            <p className={styles.subtitle}>Identify trends and patterns</p>
          </div>
        </div>
        <div className={styles.emptyState}>
          <p>No patterns detected in the data</p>
          {rawText && <RawFallback rawText={rawText} accentColor="#22c55e" />}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerIcon}>
          <FontAwesomeIcon icon={faSearch} />
        </div>
        <div>
          <h2 className={styles.title}>Pattern Detection</h2>
          <p className={styles.subtitle}>Identify trends and patterns</p>
        </div>
      </div>

      <div className={styles.patternsGrid}>
        {sections.map((sec, idx) => (
          <PatternCard key={idx} title={sec.name} icon={sec.icon} color={sec.color} bullets={sec.bullets} />
        ))}
      </div>
    </div>
  );
};

const PatternCard = ({ title, icon, color, bullets }) => {
  const [expanded, setExpanded] = useState(true);
  const maxInitial = 4;
  const hasMore = bullets.length > maxInitial;
  const visible = expanded ? bullets : bullets.slice(0, maxInitial);

  return (
    <div className={styles.patternCard}>
      <button
        type="button"
        className={styles.cardHeader}
        onClick={() => setExpanded((e) => !e)}
        aria-expanded={expanded}
      >
        <FontAwesomeIcon icon={icon} className={styles.cardIcon} style={{ color }} />
        <h3 className={styles.cardTitle}>{title}</h3>
        {hasMore && (
          <span className={styles.expandHint}>{expanded ? "−" : "+"}</span>
        )}
      </button>
      <ul className={styles.patternList}>
        {visible.map((pattern, idx) => (
          <li key={idx} className={styles.patternItem}>
            <span className={styles.bulletPoint}>•</span>
            <span>{truncate(pattern)}</span>
          </li>
        ))}
      </ul>
      {hasMore && !expanded && (
        <button type="button" className={styles.showMore} onClick={() => setExpanded(true)}>
          Show {bullets.length - maxInitial} more
        </button>
      )}
    </div>
  );
};

export default CSVPatternDetection;
